import React, {
  createContext,
  Suspense,
  useContext,
  useRef,
  useState
} from 'react'
import { Applications, ApplicationViews } from '../../../../applications'
import Loading from '../../../../components/loading/Loading'
import { CloseConfirmAlert } from './DesktopWindow'
import { useTranslation } from 'react-i18next'
import { Box, LinearProgress } from '@mui/material'
import ErrorPage from 'src/pages/errorPage/ErrorPage'
import DesktopWindowErrorBoundary from './DesktopWindowErrorBoundary'
interface propsI {
  app: keyof typeof Applications
  windowId: string
  setAlertConfirmOnClose: React.Dispatch<
    React.SetStateAction<CloseConfirmAlert | undefined>
  >
  windowContainerRef: React.RefObject<HTMLDivElement>
  iframe?: {
    title: string
    url: string
  }
  iframeRef?: React.RefObject<HTMLIFrameElement>
}

interface DesktopWindowContext {
  setWindowLoading: React.Dispatch<React.SetStateAction<boolean>>
  windowId: string
  setAlertConfirmOnClose: (
    confirmAlert: Partial<CloseConfirmAlert> | undefined
  ) => void
  windowContainerRef: React.RefObject<HTMLDivElement>
  windowRendererContainerEl: React.RefObject<HTMLDivElement>
  moveToErrorPage: () => void
  hideScrollBar: () => void
  showScrollBar: () => void
}

const desktopWindowContext = createContext<DesktopWindowContext>(
  {} as DesktopWindowContext
)

const DesktopWindowBody = ({
  showErrorPage,
  app,
  iframe,
  windowId,
  iframeRef
}: {
  showErrorPage: boolean
  app: keyof typeof Applications
  iframe?: {
    title: string
    url: string
  }
  windowId: string
  iframeRef?: React.RefObject<HTMLIFrameElement>
}) => {
  if (showErrorPage) {
    return <ErrorPage />
  }
  if (app && ApplicationViews[app]) {
    return ApplicationViews[app]
  }
  if (iframe) {
    return (
      <iframe
        title={iframe.title}
        src={iframe.url}
        frameBorder='0'
        width='100%'
        height='100%'
        allow='clipboard-read; clipboard-write'
        key={`${windowId}-iframeEl`}
        ref={iframeRef}
        id={`${windowId}-iframeEl`}
        name={`${windowId}-iframeEl`}
      ></iframe>
    )
  }
  return null
}

const DesktopWindowRenderer: ReactFC<propsI> = ({
  app,
  windowId,
  windowContainerRef,
  iframe,
  iframeRef,
  ...props
}) => {
  const { t } = useTranslation()

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [windowLoading, setWindowLoading] = useState(false)
  const [showErrorPage, setShowErrorPage] = useState(false)
  const [isScrollbarHide, setHideScrollBar] = useState(false)

  const windowRendererContainerEl = useRef<HTMLDivElement>()

  const moveToErrorPage = () => {
    setShowErrorPage(true)
  }

  // const renderer = () => {
  //   // {loading && (
  //   //   <div className={styles.loadingContainer}>
  //   //     <Loading />
  //   //   </div>
  //   // )}
  //   if (showErrorPage) {
  //     return <ErrorPage />
  //   }
  //   if (app && ApplicationViews[app]) {
  //     return ApplicationViews[app]
  //   }
  //   if (iframe) {
  //     return (
  //       <iframe
  //         title={iframe.title}
  //         src={iframe.url}
  //         frameBorder='0'
  //         width='100%'
  //         height='100%'
  //         allow='clipboard-read; clipboard-write'
  //         ref={windowId}
  //       />
  //     )
  //   }
  //   return null
  // }

  const setAlertConfirmOnClose = (
    confirmAlert: Partial<CloseConfirmAlert> | undefined
  ) => {
    if (confirmAlert === undefined) {
      return props.setAlertConfirmOnClose(undefined)
    }
    props.setAlertConfirmOnClose({
      title:
        confirmAlert.title ??
        t('apps.users.user_details_page.dialogs.changes_to_save.title'),
      message:
        confirmAlert.message ??
        t('apps.users.user_details_page.dialogs.changes_to_save.message'),
      aceptBtn:
        confirmAlert.aceptBtn ??
        t('apps.users.user_details_page.dialogs.changes_to_save.agree'),
      cancelBtn:
        confirmAlert.cancelBtn ??
        t('apps.users.user_details_page.dialogs.changes_to_save.disagree')
    })
  }

  const hideScrollBar = () => {
    setHideScrollBar(true)
  }

  const showScrollBar = () => {
    setHideScrollBar(false)
  }

  return (
    <desktopWindowContext.Provider
      value={{
        setWindowLoading,
        windowId,
        setAlertConfirmOnClose,
        windowContainerRef,
        windowRendererContainerEl:
          windowRendererContainerEl as React.RefObject<HTMLDivElement>,
        moveToErrorPage,
        hideScrollBar,
        showScrollBar
      }}
    >
      <Box
        ref={windowRendererContainerEl}
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          bgcolor: 'background.default',
          color: 'text.primary',
          overflow: iframe ? 'hidden' : 'auto',
          overflowY: iframe ? 'hidden' : 'scroll',
          ...(isScrollbarHide && {
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none',
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          })
        }}
      >
        {windowLoading && (
          <Box
            sx={{
              position: 'sticky',
              width: '100%',
              top: '0',
              height: '0',
              zIndex: 200
            }}
          >
            <LinearProgress />
          </Box>
        )}
        <DesktopWindowErrorBoundary>
          <Suspense fallback={<Loading />}>
            <DesktopWindowBody
              app={app}
              showErrorPage={showErrorPage}
              windowId={windowId}
              iframe={iframe}
              iframeRef={iframeRef}
            />
          </Suspense>
        </DesktopWindowErrorBoundary>
      </Box>
      {/* {
        windowAlert && (
          
        )
      } */}
    </desktopWindowContext.Provider>
  )
}
export default React.memo(DesktopWindowRenderer)

export const useDesktopWindowContext = () => useContext(desktopWindowContext)
